/**
 * AudioFileWidget component.
 * @module govbr.frontend.policy/widgets/AudioFileWidget
 */

import React from 'react';
import loadable from '@loadable/component';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import { validateFileUploadSize } from '@plone/volto/helpers/FormValidation/FormValidation';
import { messages as messageLabels } from '@plone/volto/helpers/MessageLabels/MessageLabels';
import {
  AUDIO_EXTENSIONS,
  AUDIO_MIME_TYPES,
} from 'govbr.frontend.policy/helpers/validations';
import { readAsDataURL } from 'promise-file-reader';
import PropTypes from 'prop-types';
import { defineMessages, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Button, Dimmer } from 'semantic-ui-react';

import deleteSVG from '@plone/volto/icons/delete.svg';

const Dropzone = loadable(() => import('react-dropzone'));

const messages = defineMessages({
  releaseDrag: {
    id: 'Drop files here ...',
    defaultMessage: 'Drop files here ...',
  },
  editFile: {
    id: 'Drop file here to replace the existing file',
    defaultMessage: 'Drop file here to replace the existing file',
  },
  fileDrag: {
    id: 'Drop file here to upload a new file',
    defaultMessage: 'Drop file here to upload a new file',
  },
  replaceFile: {
    id: 'Replace existing file',
    defaultMessage: 'Replace existing file',
  },
  addNewFile: {
    id: 'Choose a file',
    defaultMessage: 'Choose a file',
  },
  maxSizeError: {
    id: 'The file you uploaded exceeded the maximum allowed size of {size} bytes',
    defaultMessage:
      'The file you uploaded exceeded the maximum allowed size of {size} bytes',
  },
  acceptError: {
    id: 'Invalid audio file extension. Allowed types: {accept}',
    defaultMessage: 'Invalid audio file extension. Allowed types: {accept}',
  },
  invalidAudioMimeType: {
    id: 'Invalid audio file MIME type: {actual}. Allowed MIME types: {types}.',
    defaultMessage:
      'Invalid audio file MIME type: {actual}. Allowed MIME types: {types}.',
  },
  deleteAudio: {
    id: 'Delete audio file',
    defaultMessage: 'Delete audio file',
  },
});

const DEFAULT_ACCEPT = [
  ...AUDIO_MIME_TYPES,
  ...AUDIO_EXTENSIONS.map((ext) => `.${ext}`),
];

const AudioFileWidget = (props) => {
  const { id, value, onChange, isDisabled, size, accept } = props;
  const intl = useIntl();

  const acceptTypes = React.useMemo(() => {
    if (accept) {
      return accept;
    }
    return DEFAULT_ACCEPT.join(',');
  }, [accept]);

  const handleMimeTypeValidation = React.useCallback(
    (file) => {
      if (!file?.type) {
        return true;
      }

      if (!AUDIO_MIME_TYPES.includes(file.type)) {
        toast.error(
          <Toast
            error
            title={intl.formatMessage(messageLabels.error)}
            content={intl.formatMessage(messages.invalidAudioMimeType, {
              actual: file.type,
              types: AUDIO_MIME_TYPES.join(', '),
            })}
          />,
        );
        return false;
      }
      return true;
    },
    [intl],
  );

  const onDrop = (files, rejectedFiles) => {
    rejectedFiles.forEach((file) => {
      file.errors.forEach((err) => {
        if (err.code === 'file-too-large') {
          toast.error(
            <Toast
              error
              title={intl.formatMessage(messages.maxSizeError, {
                size: props.size,
              })}
            />,
          );
        }

        if (err.code === 'file-invalid-type') {
          toast.error(
            <Toast
              error
              title={intl.formatMessage(messages.acceptError, {
                accept: AUDIO_EXTENSIONS.join(', '),
              })}
            />,
          );
        }
      });
    });

    if (files.length < 1) return;

    const file = files[0];
    const isValidFile = validateFileUploadSize(file, intl.formatMessage, {
      allowedExtensions: AUDIO_EXTENSIONS,
    });

    if (!isValidFile) return;
    if (!handleMimeTypeValidation(file)) return;

    readAsDataURL(file).then((data) => {
      const fields = data.match(/^data:(.*);(.*),(.*)$/);
      onChange(id, {
        data: fields[3],
        encoding: fields[2],
        'content-type': fields[1],
        filename: file.name,
      });
    });
  };

  return (
    <FormFieldWrapper {...props} accept={acceptTypes}>
      <Dropzone
        onDrop={onDrop}
        {...(size ? { maxSize: size } : {})}
        {...(acceptTypes ? { accept: acceptTypes } : {})}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div className="file-widget-dropzone" {...getRootProps()}>
            {isDragActive && <Dimmer active></Dimmer>}
            <div className="dropzone-placeholder">
              {isDragActive ? (
                <p className="dropzone-text">
                  {intl.formatMessage(messages.releaseDrag)}
                </p>
              ) : value ? (
                <p className="dropzone-text">
                  {intl.formatMessage(messages.editFile)}
                </p>
              ) : (
                <p className="dropzone-text">
                  {intl.formatMessage(messages.fileDrag)}
                </p>
              )}
            </div>

            <label className="label-file-widget-input">
              {value
                ? intl.formatMessage(messages.replaceFile)
                : intl.formatMessage(messages.addNewFile)}
            </label>
            <input
              {...getInputProps({
                type: 'file',
                style: { display: 'none' },
              })}
              id={`field-${id}`}
              name={id}
              type="file"
              accept={acceptTypes}
              disabled={isDisabled}
            />
          </div>
        )}
      </Dropzone>
      <div className="field-file-name">
        {value && (
          <UniversalLink href={value.download} download={true}>
            {value.filename}
          </UniversalLink>
        )}
        {value && (
          <Button
            type="button"
            icon
            basic
            className="delete-button"
            aria-label={intl.formatMessage(messages.deleteAudio)}
            disabled={isDisabled}
            onClick={() => {
              onChange(id, null);
            }}
          >
            <Icon name={deleteSVG} size="20px" />
          </Button>
        )}
      </div>
    </FormFieldWrapper>
  );
};

AudioFileWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.shape({
    '@type': PropTypes.string,
    title: PropTypes.string,
    download: PropTypes.string,
    filename: PropTypes.string,
    'content-type': PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  wrapped: PropTypes.bool,
  isDisabled: PropTypes.bool,
  size: PropTypes.number,
  accept: PropTypes.string,
};

AudioFileWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  isDisabled: false,
  size: undefined,
  accept: undefined,
};

export default AudioFileWidget;
